import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Home, ClipboardCheck, BarChart3, Settings, LogOut, Menu, X } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../ui/Button';
import { HybridAvatar } from '../ui/Avatar';

export const MinimalHeader: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMenuOpen(false);
  };

  // Check if user is admin
  const isAdmin = user?.subscription_tier === 'enterprise' || 
                  user?.email === 'admin@suhfreitas.com.br' ||
                  user?.roles?.includes('admin');

  // Navigation items for authenticated users
  const navigationItems = [
    { label: 'Início', href: '/dashboard', icon: Home },
    { label: 'Avaliação', href: '/assessment', icon: ClipboardCheck },
    { label: 'Meus Resultados', href: '/results', icon: BarChart3 },
  ];

  // Add admin option if user is admin
  if (isAdmin) {
    navigationItems.push({ label: 'Admin', href: '/admin', icon: Settings });
  }

  const isActiveRoute = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  const getTierInfo = () => {
    switch (user?.subscription_tier) {
      case 'premium':
        return {
          name: 'Premium',
          color: 'from-primary-500 to-primary-700'
        };
      case 'mentoring':
        return {
          name: 'Mentoring',
          color: 'from-primary-600 to-primary-800'
        };
      case 'enterprise':
        return {
          name: 'Enterprise',
          color: 'from-primary-700 to-primary-900'
        };
      default:
        return null;
    }
  };

  const tierInfo = getTierInfo();

  return (
    <header className="fixed top-0 left-0 right-0 bg-white border-b border-neutral-200 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="w-12 h-12 flex items-center justify-center transition-all duration-300 group-hover:scale-105">
              <img 
                src="/logo-sf-main.svg" 
                alt="Suh Freitas Logo" 
                className="w-12 h-12 object-contain"
              />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent group-hover:scale-105 transition-transform duration-300">
                Suh Freitas
              </h1>
              <div className="text-xs text-neutral-600 font-medium">
                Mentoria & Desenvolvimento
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {isAuthenticated ? navigationItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`
                    relative inline-flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 group
                    ${isActiveRoute(item.href)
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-neutral-700 hover:text-primary-600 hover:bg-primary-50'
                    }
                  `}
                >
                  <IconComponent size={16} />
                  <span>{item.label}</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-500/0 via-primary-500/5 to-primary-500/0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Link>
              );
            }) : null}
          </nav>

          {/* Auth Section */}
          <div className="flex items-center space-x-4">
            {isAuthenticated && user ? (
              <div className="flex items-center space-x-4">
                {/* User Info - Desktop com tier badge */}
                <div className="hidden sm:flex items-center space-x-3">
                  <HybridAvatar
                    name={user?.name || 'Usuário'}
                    src={user?.profile_image || user?.ProfileImageURL}
                    size="sm"
                  />
                  <div className="text-sm">
                    <div className="font-medium text-neutral-900">
                      {user?.name?.split(' ')[0] || 'Usuário'}
                    </div>
                    {tierInfo && (
                      <div className={`text-xs font-medium bg-gradient-to-r ${tierInfo.color} bg-clip-text text-transparent`}>
                        {tierInfo.name}
                      </div>
                    )}
                  </div>
                </div>

                {/* Logout */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="text-neutral-600 hover:text-neutral-900 inline-flex items-center space-x-2"
                >
                  <LogOut size={16} />
                  <span>Sair</span>
                </Button>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Link to="/login">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="font-medium hover:border-primary-300 hover:bg-primary-50 transition-all duration-300"
                  >
                    Entrar
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    variant="primary"
                    size="sm"
                    className="font-semibold"
                  >
                    Cadastrar
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-300"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-6 border-t border-neutral-200 bg-white">
            <nav className="space-y-2">
              {isAuthenticated && navigationItems.map((item) => {
                const IconComponent = item.icon;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className={`
                      flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-300 font-medium
                      ${isActiveRoute(item.href)
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-neutral-700 hover:text-primary-600 hover:bg-gradient-to-r hover:from-primary-50 hover:to-secondary-50'
                      }
                    `}
                  >
                    <IconComponent size={20} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
              
              {isAuthenticated && user && (
                <>
                  <div className="border-t border-primary-100/50 pt-4 mt-4">
                    <div className="flex items-center space-x-3 px-4 py-3 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl">
                      <HybridAvatar
                        name={user?.name || 'Usuário'}
                        src={user?.profile_image || user?.ProfileImageURL}
                        size="md"
                      />
                      <div className="text-sm">
                        <div className="font-semibold text-neutral-900">{user?.name || 'Usuário'}</div>
                        <div className="text-neutral-600">{user?.email || ''}</div>
                        {tierInfo && (
                          <div className={`text-xs font-medium bg-gradient-to-r ${tierInfo.color} bg-clip-text text-transparent`}>
                            {tierInfo.name}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Mobile Logout */}
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium text-neutral-600 hover:text-red-600 hover:bg-red-50 transition-all duration-200 w-full text-left"
                  >
                    <LogOut size={20} />
                    <span>Sair</span>
                  </button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};